/**
 * Product Grid Swiper using Flickity
 * Handles swipeable image galleries for product grid items
 */

(function() {
  'use strict';

  // Prevent multiple initializations to avoid memory leaks
  if (window._productGridSwiperInitialized) {
    return;
  }
  window._productGridSwiperInitialized = true;

  // Check if Flickity is available
  const Flickity = window.Flickity || window.themeVendor?.Flickity;
  
  if (!Flickity) {
    console.error('[Product Grid Swiper] Flickity library not found');
    return;
  }

  // Store initialized carousels
  const initializedSwipers = new WeakMap();
  // Keep track of swiper elements for iteration
  const swiperElements = new Set();

  // Initialize all product grid swipers
  function initProductGridSwipers() {
    const swipers = document.querySelectorAll('.product-grid-swiper:not(.flickity-enabled)');
    
    
    swipers.forEach((swiperElement) => {
      // Skip if already initialized
      if (initializedSwipers.has(swiperElement)) {
        return;
      }
      
      const slidesCount = swiperElement.querySelectorAll('.product-grid-swiper-slide').length;
      
      // Only initialize if there are multiple slides
      if (slidesCount <= 1) {
        return;
      }
      
      
      try {
        let isDragging = false;
        
        // Initialize Flickity
        const flkty = new Flickity(swiperElement, {
          cellSelector: '.product-grid-swiper-slide',
          contain: false,
          wrapAround: true,
          pageDots: true,
          prevNextButtons: true,
          draggable: true,
          adaptiveHeight: false,
          setGallerySize: false,
          imagesLoaded: false,
          lazyLoad: false,
          percentPosition: false,
          cellAlign: 'left',
          friction: 0.8,
          selectedAttraction: 0.15,
          accessibility: true,
          resize: true,
          groupCells: false,
          freeScroll: false,
        });

        // Mark as initialized
        initializedSwipers.set(swiperElement, flkty);
        swiperElements.add(swiperElement);

        // Prevent link navigation when dragging
        const productLink = swiperElement.closest('.product-link');
        if (productLink) {
          flkty.on('dragStart', function() {
            isDragging = true;
          });
          
          flkty.on('dragEnd', function() {
            setTimeout(function() {
              isDragging = false;
            }, 100);
          });
          
          productLink.addEventListener('click', function(e) {
            if (isDragging) {
              e.preventDefault();
              e.stopPropagation();
            }
          }, true);
        }

        // Resize after initialization
        setTimeout(function() {
          flkty.resize();
        }, 100);

      } catch (error) {
        console.error('[Product Grid Swiper] Error initializing Flickity:', error);
      }
    });
  }

  // Initialize on page load - call immediately for faster initialization
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initProductGridSwipers();
      // Quick follow-up check for any late-loaded elements
      setTimeout(initProductGridSwipers, 100);
    });
  } else {
    // Document already loaded, initialize immediately
    requestAnimationFrame(function() {
      initProductGridSwipers();
      // Quick follow-up check
      setTimeout(initProductGridSwipers, 100);
    });
  }

  // Re-initialize on dynamic content load
  window.addEventListener('wnw_load', function() {
    initProductGridSwipers();
    observeProductGrids(); // Re-observe in case new containers were added
  });
  window.addEventListener('load', function() {
    initProductGridSwipers();
    observeProductGrids(); // Re-observe in case new containers were added
  });
  document.addEventListener('theme:resize:width', function() {
    // Resize all existing Flickity instances
    swiperElements.forEach(function(swiperElement) {
      const flkty = initializedSwipers.get(swiperElement);
      if (flkty && flkty.resize) {
        flkty.resize();
      }
    });
  });

  // Observer for dynamically loaded products
  // Store observer and observed elements to prevent memory leaks
  const observedElements = new WeakSet();
  
  // Create observer for product changes within grids (only created once due to script-level guard)
  const productGridObserver = new MutationObserver(function(mutations) {
    let shouldInit = false;
    let shouldObserveNewGrids = false;
    
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) {
            // Check if new product items/swipers were added
            if (node.classList?.contains('product-grid-swiper') || 
                node.classList?.contains('product-item') ||
                node.classList?.contains('grid-item') ||
                node.querySelector?.('.product-grid-swiper')) {
              shouldInit = true;
            }
            
            // Check if a new product grid container was added
            if (node.classList?.contains('collection__products') ||
                node.classList?.contains('product-grid') ||
                node.classList?.contains('featured-collection') ||
                node.classList?.contains('grid') ||
                node.hasAttribute('data-collection-products') ||
                node.querySelector?.('.collection__products, .product-grid, .featured-collection, .grid, [data-collection-products]')) {
              shouldObserveNewGrids = true;
            }
          }
        });
      }
    });
    
    if (shouldInit) {
      setTimeout(initProductGridSwipers, 100);
    }
    
    // If new grid containers were added, observe them
    if (shouldObserveNewGrids) {
      setTimeout(observeProductGrids, 100);
    }
  });

  // Observe product grid containers
  const observeProductGrids = function() {
    const productGrids = document.querySelectorAll('.collection__products, .product-grid, .featured-collection, .grid, [data-collection-products]');
    productGrids.forEach(function(grid) {
      // Only observe if not already being observed to prevent memory leaks
      if (!observedElements.has(grid)) {
        observedElements.add(grid);
        productGridObserver.observe(grid, {
          childList: true,
          subtree: true,
        });
      }
    });
  };

  // Main observer to watch for new grid containers being added to the page
  const mainObserver = new MutationObserver(function(mutations) {
    let newGridsFound = false;
    
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) {
            // Check if a new product grid container was added
            if (node.classList?.contains('collection__products') ||
                node.classList?.contains('product-grid') ||
                node.classList?.contains('featured-collection') ||
                node.classList?.contains('grid') ||
                node.hasAttribute('data-collection-products') ||
                node.querySelector?.('.collection__products, .product-grid, .featured-collection, .grid, [data-collection-products]')) {
              newGridsFound = true;
            }
          }
        });
      }
    });
    
    if (newGridsFound) {
      observeProductGrids();
    }
  });

  // Store observers for cleanup
  window._productGridSwiperObservers = {
    main: mainObserver,
    productGrid: productGridObserver
  };

  // Initialize observers
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      observeProductGrids();
      // Watch document body for new grid containers
      if (document.body) {
        mainObserver.observe(document.body, {
          childList: true,
          subtree: true,
        });
      }
    });
  } else {
    observeProductGrids();
    // Watch document body for new grid containers
    if (document.body) {
      mainObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }
  
  // Cleanup on page unload to prevent memory leaks
  window.addEventListener('beforeunload', function() {
    if (window._productGridSwiperObservers) {
      if (window._productGridSwiperObservers.main) {
        window._productGridSwiperObservers.main.disconnect();
      }
      if (window._productGridSwiperObservers.productGrid) {
        window._productGridSwiperObservers.productGrid.disconnect();
      }
      window._productGridSwiperObservers = null;
    }
  });

  // Re-initialize on AJAX/Infinite Scroll pagination
  if (window.Shopify && window.Shopify.theme) {
    document.addEventListener('shopify:section:load', function() {
      initProductGridSwipers();
      observeProductGrids(); // Re-observe in case new containers were added
    });
  }
  
  // Listen for collection filter/sort changes
  document.addEventListener('collection:updated', function() {
    initProductGridSwipers();
    observeProductGrids(); // Re-observe in case new containers were added
  });

  // For collection pages - quick initial checks then reduce frequency
  if (document.body.classList.contains('template-collection')) {
    let checkCount = 0;
    // First 2 checks happen quickly to catch early loads
    const quickCheck = function() {
      initProductGridSwipers();
      checkCount++;
      if (checkCount < 2) {
        setTimeout(quickCheck, 50);
      } else if (checkCount < 4) {
        setTimeout(quickCheck, 300);
      }
    };
    quickCheck();
  }

  // Expose for debugging
  window.initProductGridSwipers = initProductGridSwipers;
})();
